import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { set, unset } from 'es-toolkit/compat';
import { createAPIError, isAPIError } from 'shared/api/APIError';
import { APIMutationOptions, useAPIMutation } from 'shared/api/APIMutation';
import { useAPIQuery } from 'shared/api/APIQuery';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import {
  requestJSONLoadboardAPI,
  requestLoadboardAPI,
} from 'shared/api/LoadboardAPIClient';
import { deepClone } from 'shared/utils/DataUtils';
import {
  compareSavedSearch,
  normalizeSavedSearchPayment,
  PostingSavedSearchAlertDTO,
  PostingSavedSearchDTO,
  PostingSavedSearchPayloadDTO,
  postingSavedSearchPayloadSchemaPayload,
  postingSavedSearchSchema,
} from './dto/PostingSavedSearchDTO';

function normalizePayload(value: PostingSavedSearchPayloadDTO) {
  const isSuperPaySelected = value.search_criteria.payment.terms === 'superpay';
  const plain = postingSavedSearchPayloadSchemaPayload.cast(deepClone(value));

  if (isSuperPaySelected) {
    unset(plain, 'search_criteria.payment.terms');
    set(plain, 'search_criteria.payment.method', 'superpay');
  } else {
    unset(plain, 'search_criteria.payment.method');
  }
  unset(plain, 'search_criteria.posted_to_private_loadboard');
  unset(plain, 'search_criteria.show_bookable');
  unset(plain, 'search_criteria.search_along_route');
  unset(plain, 'search_criteria.distance_off_route');
  unset(plain, 'search_criteria.latr_waypoints');

  return plain;
}

export function useSavedSearch(guid: string | undefined) {
  return useAPIQuery(
    ['saved-searches', 'item', { guid }],
    () =>
      requestJSONLoadboardAPI<APIResponse<PostingSavedSearchDTO>>([
        'GET /internal/v3/saved_searches/{guid}',
        { guid },
      ]),
    {
      normalize: (data: unknown) =>
        normalizeSavedSearchPayment(postingSavedSearchSchema.cast(data)),
      enabled: !!guid,
    },
  );
}

const SAVED_SEARCHES_QUERY_KEY = ['saved-searches', 'list'];

function normalizeSavedSearches(data: unknown[]) {
  return data
    .map((item) =>
      normalizeSavedSearchPayment(postingSavedSearchSchema.cast(item)),
    )
    .sort((a, b) => compareSavedSearch(a, b));
}

export function useSavedSearches() {
  return useAPIQuery(
    SAVED_SEARCHES_QUERY_KEY,
    () =>
      requestJSONLoadboardAPI<APIResponse<PostingSavedSearchDTO[]>>(
        'GET /internal/v3/saved_searches',
      ),
    {
      normalize: normalizeSavedSearches,
    },
  );
}

export function useCreateSavedSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (value: PostingSavedSearchPayloadDTO) => {
      return requestJSONLoadboardAPI<APIResponse<PostingSavedSearchDTO>>(
        'POST /internal/v3/saved_searches',
        { json: normalizePayload(value) },
      ).then((response) => postingSavedSearchSchema.cast(response.data));
    },
    onSuccess: (newSavedSearch) => {
      queryClient.setQueryData<PostingSavedSearchDTO[]>(
        SAVED_SEARCHES_QUERY_KEY,
        (old = []) => normalizeSavedSearches([...old, newSavedSearch]),
      );

      return newSavedSearch;
    },
    onError: (error) =>
      Promise.reject(
        isAPIError(error) && error.type === 'SAVED_SEARCH_LIMIT_EXCEEDED'
          ? createAPIError({ ...error, message: 'Exceeded Limit of 50' })
          : error,
      ),
  });
}

export function editSavedSearch(
  guid: string,
  value: PostingSavedSearchPayloadDTO,
) {
  return requestJSONLoadboardAPI<APIResponse<PostingSavedSearchDTO>>(
    ['PUT /internal/v3/saved_searches/{guid}', { guid }],
    { json: normalizePayload(value) },
  ).then((response) => postingSavedSearchSchema.cast(response.data));
}

export function useDeleteSavedSearch(
  options?: UseMutationOptions<Response, Error, string>,
) {
  return useMutation({
    mutationFn: (guid) =>
      requestLoadboardAPI([
        'DELETE /internal/v3/saved_searches/{guid}',
        { guid },
      ]),
    ...options,
  });
}

export function useEditSavedSearchAlert(
  options?: APIMutationOptions<
    [savedSearch: PostingSavedSearchDTO, alert: PostingSavedSearchAlertDTO]
  >,
) {
  return useAPIMutation(
    ([{ guid }, alert]) =>
      requestJSONLoadboardAPI(
        ['PUT /internal/v3/saved_searches/{guid}/alert', { guid }],
        {
          json: alert,
        },
      ),
    options,
  );
}
